import React, { createContext, useContext, useEffect, useState } from "react";

// ---- Types ----
type TabsContextValue = {
    value: string;
    onValueChange: (value: string) => void;
};

// ---- Context ----
const TabsContext = createContext<TabsContextValue | undefined>(undefined);

function useTabs() {
    const context = useContext(TabsContext);
    if (!context) {
        throw new Error("Tabs components must be used within a Tabs component");
    }
    return context;
}

// ---- Tabs ----
interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
    defaultValue?: string;
    value?: string;
    onValueChange?: (value: string) => void;
    orientation?: "horizontal" | "vertical";
    children: React.ReactNode;
}

const Tabs = ({
    defaultValue,
    value,
    onValueChange,
    orientation = "horizontal",
    className = "",
    children,
    ...props
}: TabsProps) => {
    const [tabValue, setTabValue] = useState(defaultValue || "");

    // Handle controlled component case
    useEffect(() => {
        if (value !== undefined) {
            setTabValue(value);
        }
    }, [value]);

    const handleValueChange = (newValue: string) => {
        if (value === undefined) {
            // Only update internal state for uncontrolled component
            setTabValue(newValue);
        }
        onValueChange?.(newValue);
    };

    return (
        <TabsContext.Provider
            value={{
                value: value ?? tabValue,
                onValueChange: handleValueChange,
            }}
        >
            <div
                role="tablist"
                aria-orientation={orientation}
                className={`w-full ${className}`}
                {...props}
            >
                {children}
            </div>
        </TabsContext.Provider>
    );
};

// ---- TabsList ----
interface TabsListProps extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode;
}

const TabsList = ({ className = "", children, ...props }: TabsListProps) => {
    return (
        <div
            className={`flex items-center p-1 rounded-md bg-gray-100 ${className}`}
            {...props}
        >
            {children}
        </div>
    );
};

// ---- TabsTrigger ----
interface TabsTriggerProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    value: string;
    disabled?: boolean;
    children: React.ReactNode;
}

const TabsTrigger = ({
    value,
    disabled = false,
    className = "",
    children,
    ...props
}: TabsTriggerProps) => {
    const { value: selectedValue, onValueChange } = useTabs();
    const isSelected = selectedValue === value;

    return (
        <button
            type="button"
            role="tab"
            aria-selected={isSelected}
            aria-controls={`tab-content-${value}`}
            tabIndex={isSelected ? 0 : -1}
            disabled={disabled}
            className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:shadow-sm ${
                isSelected
                    ? "bg-white shadow-sm text-gray-900"
                    : "text-gray-600 hover:text-gray-900"
            } ${className}`}
            onClick={() => onValueChange(value)}
            data-state={isSelected ? "active" : "inactive"}
            {...props}
        >
            {children}
        </button>
    );
};

// ---- TabsContent ----
interface TabsContentProps extends React.HTMLAttributes<HTMLDivElement> {
    value: string;
    forceMount?: boolean;
    children: React.ReactNode;
}

const TabsContent = ({
    value,
    forceMount = false,
    className = "",
    children,
    ...props
}: TabsContentProps) => {
    const { value: selectedValue } = useTabs();
    const isSelected = selectedValue === value;

    if (!isSelected && !forceMount) {
        return null;
    }

    return (
        <div
            role="tabpanel"
            id={`tab-content-${value}`}
            aria-labelledby={`tab-trigger-${value}`}
            tabIndex={0}
            className={`focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                isSelected ? "animate-in fade-in-0 zoom-in-95" : "hidden"
            } ${className}`}
            data-state={isSelected ? "active" : "inactive"}
            {...props}
        >
            {children}
        </div>
    );
};

export { Tabs, TabsList, TabsTrigger, TabsContent };
